import { DefectCode } from "../../types/defect";
import { Button } from "primereact/button";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { useCallback, useMemo, useState, useEffect } from "react";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { DefectCodeFilter } from "./DefectCodeFilter";
import { Dialog } from "primereact/dialog";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectDefectCodes, selectDefectCodeCategories } from "../../store/selectors/defectSelectors";
import { GenericCodeReasonDialog } from "./GenericCodeReasonDialog";

type Props = {
  onHide: () => void;
  onSubmit: (code: string | null, codeAssignReason?: string | null) => Promise<void>;
  visible: boolean;
  currentTypeCode?: string | null;
};

export const DefectCodeDialog = (props: Props) => {
  const { onHide, onSubmit, visible, currentTypeCode } = props;

  const [selectedDefectCode, setSelectedDefectCode] = useState<DefectCode | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showReasonDialog, setShowReasonDialog] = useState<boolean>(false);
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    category: { value: null, matchMode: FilterMatchMode.CONTAINS },
    subcategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const defectCodes = useAppSelector(selectDefectCodes);
  const categories = useAppSelector(selectDefectCodeCategories);
  const cleanedDefectCodes = useMemo(() => 
    defectCodes.map(item => ({
      ...item,
      category: item.category ?? "",
      subCategory: item.subCategory ?? ""
    })), [defectCodes]);

  function getCodeCategory(code: string) {
    if (categories && categories[code]) {
      return categories[code];
    }

    return "General";
  }

  // Auto-select filter based on current typeCode
  useEffect(() => {
    if (visible) {
      // Reset filters first
      let _filters = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        category: { value: null, matchMode: FilterMatchMode.CONTAINS },
        subcategory: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
      
      // Auto-select filter if we have a typeCode with area prefix
      if (currentTypeCode && currentTypeCode.includes('.')) {
        const category = getCodeCategory(currentTypeCode);
        // @ts-ignore
        _filters["category"].value = category;
        // @ts-ignore
        _filters["code"].value = currentTypeCode.split('.')[0];
      }
      
      setFilters(_filters);
      setGlobalFilterValue("");
      setSelectedDefectCode(null);
    }
  }, [currentTypeCode, visible]);

  const onGlobalFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      let _filters = { ...filters };

      // @ts-ignore
      _filters["global"].value = value;

      setFilters(_filters);
      setGlobalFilterValue(value);
    },
    [filters]
  );

  const isGenericCode = useCallback((code: string) => {
    return code.includes("000");
  }, []);

  const onAssign = useCallback(async () => {
    if (!selectedDefectCode?.code) return;
    
    if (isGenericCode(selectedDefectCode.code)) {
      setShowReasonDialog(true);
      return;
    }
    
    setLoading(true);
    await onSubmit(selectedDefectCode.code, null);
    setLoading(false);
    onHide();
  }, [selectedDefectCode, isGenericCode, onSubmit, onHide]);

  const onReasonSubmit = useCallback(async (reason: string) => {
    if (!selectedDefectCode?.code) return;
    
    setShowReasonDialog(false);
    setLoading(true);
    await onSubmit(selectedDefectCode.code, reason);
    setLoading(false);
    onHide();
  }, [selectedDefectCode, onSubmit, onHide]);

  const onReasonCancel = useCallback(() => {
    setShowReasonDialog(false);
  }, []);

  const headerContent = useMemo(
    () => (
      <div>
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Keyword Search"
        />
      </div>
    ),
    [globalFilterValue]
  );

  const footerContent = useMemo(
    () => (
      <Button
        label="Assign Code"
        onClick={onAssign}
        className="w-full mt-2"
        disabled={!selectedDefectCode}
        loading={loading}
      />
    ),
    [selectedDefectCode]
  );

  return (
    <>
      <Dialog
        header={"Assign Defect Code"}
        visible={visible}
        className="large-dialog"
        draggable={false}
        footer={footerContent}
        onHide={onHide}
      >
        <DataTable
          value={cleanedDefectCodes}
          selection={selectedDefectCode!}
          header={headerContent}
          globalFilterFields={["code", "shortGenName"]}
          filters={filters}
          filterDisplay="row"
          emptyMessage={"No data found"}
          onSelectionChange={(e) => setSelectedDefectCode(e.value)}
          paginator
          rows={10}
        >
          <Column selectionMode="single" headerStyle={{ width: "3rem" }}></Column>
          <Column
            key="code"
            sortable
            sortField="code"
            field="code"
            header="Code"
            filter
            showFilterMenu={false}
            filterElement={(options) => (
              <DefectCodeFilter 
                options={options} 
                onFilterChange={(value) => {
                  const _filters = { ...filters };
                  _filters.code = { value, matchMode: FilterMatchMode.CONTAINS };
                  _filters.category = { value: null, matchMode: FilterMatchMode.CONTAINS };
                  _filters.subcategory = { value: null, matchMode: FilterMatchMode.CONTAINS };
                  setFilters(_filters);
                }}
              />
            )}
          />
          <Column
            key="shortGenName"
            sortable
            sortField="shortGenName"
            field="shortGenName"
            header="Description"
          />
          <Column
            key="category"
            sortable
            sortField="category"
            field="category"
            header="Category"
          />
          <Column
            key="subCategory"
            sortable
            sortField="subCategory"
            field="subCategory"
            header="Sub Category"
          />
        </DataTable>
      </Dialog>
      
      <GenericCodeReasonDialog
        visible={showReasonDialog}
        onHide={onReasonCancel}
        onSubmit={onReasonSubmit}
        defectCode={selectedDefectCode?.code || ""}
      />
    </>
  );
};
